<script setup>
import { reactive, computed, ref, watch } from 'vue'

import { Vue3Lottie } from 'vue3-lottie'

const props = defineProps({
  email: {
    type: String,
    default: null,
  },
  emailInputWidth: {
    type: Number,
    default: 0,
  },
  isTypingEmail: {
    type: Boolean,
    default: false,
  },
  isTypingPassword: {
    type: Boolean,
    default: false,
  },
})

const lottieStandingRef = ref(null)
const lottieEmailRef = ref(null)
const lottiePasswordRef = ref(null)

const emailAnimationState = reactive({
  totalFrames: 30,
  lastFrame: 0,
  isComplete: true,
})

const passwordAnimationState = reactive({
  totalFrames: 16,
  lastFrame: 0,
  isComplete: true,
})

const isTypingEmail = computed(() => props.isTypingEmail || !emailAnimationState.isComplete)
const isTypingPassword = computed(() => props.isTypingPassword || !passwordAnimationState.isComplete)
const isStandingBy = computed(() => !isTypingEmail.value && !isTypingPassword.value)

function getTextWidth (text = '') {
  if (text.length === 0) return 0

  const helperEl = document.createElement('span')
  document.body.appendChild(helperEl)

  helperEl.style.position = 'absolute'
  helperEl.style.visibility = 'hidden'
  helperEl.style.whiteSpace = 'nowrap'
  helperEl.innerHTML = text

  const width = Math.ceil(helperEl.clientWidth)

  document.body.removeChild(helperEl)

  return width
}

watch([() => props.isTypingEmail, () => props.email], async ([isTypingEmail, email], [wasTypingEmail, prevEmail]) => {
  emailAnimationState.isComplete = false

  let frame = 10 // First frame with Dodi eyes down.
  if (email?.length > 0) {
    const remainingFrames = emailAnimationState.totalFrames - frame
    const emailInputContentWidth = props.emailInputWidth - 66
    frame = Math.min(Math.ceil(frame + (getTextWidth(props.email) * remainingFrames / emailInputContentWidth)), emailAnimationState.totalFrames)
  } else if (email?.length === 0 && wasTypingEmail & isTypingEmail) {
    emailAnimationState.lastFrame = 11
  }

  if (wasTypingEmail && !isTypingEmail) {
    emailAnimationState.lastFrame = emailAnimationState.lastFrame > 12 ? 7 : 10
    frame = 0
  }

  lottieEmailRef.value?.playSegments([emailAnimationState.lastFrame, frame], true)
  emailAnimationState.lastFrame = frame
})

watch(() => props.isTypingPassword, async isTypingPassword => {
  passwordAnimationState.isComplete = false

  if (isTypingPassword) lottiePasswordRef.value?.playSegments([0, passwordAnimationState.totalFrames], true)
  else lottiePasswordRef.value?.playSegments([passwordAnimationState.totalFrames, 0], true)
})

watch(isStandingBy, isStandingBy => {
  if (isStandingBy) setTimeout(() => lottieStandingRef.value?.play(), 100) // Used to force a smooth transition between animations.
  else lottieStandingRef.value?.stop()
})
</script>

<template>
  <div class="login-avatar-dodi">
    <Vue3Lottie
      v-show="isStandingBy"
      ref="lottieStandingRef"
      animation-link="/animations/avatar-dodi-stand-by.json"
    />
    <Vue3Lottie
      v-show="isTypingEmail && !isTypingPassword && passwordAnimationState.isComplete"
      ref="lottieEmailRef"
      animation-link="/animations/avatar-dodi-reading.json"
      :auto-play="false"
      :loop="false"
      @on-complete="emailAnimationState.isComplete = true"
    />
    <Vue3Lottie
      v-show="(isTypingPassword && !isTypingEmail) || !passwordAnimationState.isComplete"
      ref="lottiePasswordRef"
      animation-link="/animations/avatar-dodi-glasses.json"
      :auto-play="false"
      :loop="false"
      @on-complete="passwordAnimationState.isComplete = true"
    />
  </div>
</template>

<style lang="scss">
.login-avatar-dodi {
  height: 96px;
}
</style>
